import { gql } from "@apollo/client";
import GraphQlService from "./graphql";

const GET_ACCOUNTS_CACHE_TTL_MS = 3000;

class AccountGraphql extends GraphQlService {
  async getAccounts(isAllAccountsShouldBeShown = true) {
    const cached = this._getAccountsCache;
    if (
      cached &&
      cached.key === isAllAccountsShouldBeShown &&
      Date.now() - cached.timestamp < GET_ACCOUNTS_CACHE_TTL_MS
    ) {
      return cached.promise;
    }

    const promise = this.client
      .query({
        query: gql`
          query {
            findAllAccountsByUserId(isAllAccountsShouldBeShown: ${isAllAccountsShouldBeShown}) {
              id
              isUnavailable
              unavailableMessage
              isAnywaySendMessage
              account {
                ssoEnabled
                name
                id
                dtActivation
                dtDeactivation
                ids
                tutorialLink
              }
              user {
                email
                chatChatParticipants {
                  accountId
                  name
                  sendMessageCount
                  readMessageCount
                  chatRoom {
                    messageCount
                    organizerName
                  }
                }
                chatChatParticipantGlobals {
                  name
                  sendMessageCount
                  readMessageCount
                  globalChatRoom {
                    messageCount
                    organizerName
                  }
                }
              }
            }
          }
        `,
        fetchPolicy: "network-only",
      })
      .then((result) => result?.data?.findAllAccountsByUserId);

    this._getAccountsCache = {
      key: isAllAccountsShouldBeShown,
      timestamp: Date.now(),
      promise,
    };
    return promise;
  }

  async getAllAccountsByCurrentUser() {
    return this.client
      .query({
        query: gql`
          query {
            getAllAccountsByCurrentUser {
              accountId
              userId
              isUnavailable
              unavailableMessage
              isAnywaySendMessage
              account {
                ssoEnabled
                name
                id
                dtActivation
                dtDeactivation
              }
            }
          }
        `,
        fetchPolicy: "network-only",
      })
      .then((result) => result?.data?.getAllAccountsByCurrentUser);
  }

  async updateAuthUserSession(accountId) {
    return this.client
      .mutate({
        mutation: gql`
            mutation{
              updateAuthUserSession(
                accountId: """${JSON.stringify(accountId)}""")
              {
              accessToken
              refreshAccessToken
            }
          }
        `,
      })
      .then((result) => result?.data?.updateAuthUserSession);
  }

  async findChildrenAccounts() {
    return this.client
      .query({
        query: gql`
          query {
            findChildrenAccounts {
              name
              id
            }
          }
        `,
      })
      .then((result) => result?.data?.findChildrenAccounts);
  }

  async findChildrenAccountsByAccountId(accountId) {
    return this.client
      .query({
        query: gql`
        query{
          findChildrenAccountsByAccountId(
            id:"${accountId}"
            ){
            name
            id
          }
        }
        `,
      })
      .then((result) => result?.data?.findChildrenAccountsByAccountId);
  }

  async getTopParentDetail() {
    return this.client
      .query({
        query: gql`
          query {
            getTopParentDetail {
              id
              ssoEnabled
            }
          }
        `,
      })
      .then((result) => result?.data?.getTopParentDetail);
  }

  async updateIsUnavailableStatus(isUnavailable, accountId, userId) {
    return this.client
      .mutate({
        mutation: gql`
        mutation {
          updateIsUnavailableStatus(
            updateUnavailableStatusInput: { isUnavailable: ${isUnavailable}, accountId: "${accountId}", userId: "${userId}" }
          ) {
            id
          }
        }
        `,
      })
      .then((result) => result?.data?.updateIsUnavailableStatus);
  }

  async updateUnavailableMessage(message, accountId, userId) {
    return this.client
      .mutate({
        mutation: gql`
          mutation {
            updateUnavailableMessage(
              updateUnavailableMessageInput: { unavailableMessage: """${message}""", accountId: "${accountId}", userId: "${userId}" }
            ) {
              id
            }
          }
        `,
      })
      .then((result) => result?.data?.updateUnavailableMessage);
  }

  async updateIsAnywaySendMessageStatus(
    isAnywaySendMessage,
    accountId,
    userId
  ) {
    return this.client
      .mutate({
        mutation: gql`
        mutation {
          updateIsAnywaySendMessageStatus(
            updateAnywaySendMessageStatusInput: { isAnywaySendMessage: ${isAnywaySendMessage}, accountId: "${accountId}", userId: "${userId}" }
          ) {
            id
          }
        }
        `,
      })
      .then((result) => result?.data?.updateIsAnywaySendMessageStatus);
  }

  async getLeyrUserDetails(accountId, userId) {
    return this.client
      .query({
        query: gql`
          query {
            getLeyrUserDetails(
              accountId: "${accountId}"
              userId: "${userId}"
            ) {
              active
              emrName
              careUnitName
              leyrUser {
                id
                userId
                accountId
                active
              }
            }
          }
        `,
        fetchPolicy: "network-only",
      })
      .then((result) => result?.data?.getLeyrUserDetails);
  }

  async getLeyrUserInitialization(accountId) {
    return this.client
      .query({
        query: gql`
          query {
            getLeyrUserInitialization(accountId: "${accountId}") {
              availableCareUnits {
                id
                name
              }
              resource {
                name
                externalId
                emrInternalUserId
              }
            }
          }
        `,
        fetchPolicy: "network-only",
      })
      .then((result) => result?.data?.getLeyrUserInitialization);
  }

  async updateLeyrUser(id, active) {
    return this.client
      .mutate({
        mutation: gql`
          mutation {
            updateLeyrUser(
              updateLeyrUsersInput: { id: "${id}", active: ${active} }
            ) {
              id
              active
            }
          }
        `,
      })
      .then((result) => result?.data?.updateLeyrUser);
  }

  async createLeyrUserDetails(input) {
    return this.client
      .mutate({
        mutation: gql`
          mutation createLeyrUser($input: CreateLeyrUsersInput!) {
            createLeyrUserDetails(createLeyrUsersInput: $input) {
              id
              userId
              accountId
              active
              configJson
            }
          }
        `,
        variables: {
          input,
        },
      })
      .then((result) => result?.data?.createLeyrUserDetails);
  }
}

const AccountGraphqlInstance = new AccountGraphql();
export default AccountGraphqlInstance;
